import axios from 'axios';
import Account, { AccountRole } from '../domain/Account';
import { Profile } from '../domain/Profile';
import { UUID } from "../domain/UUID";

export interface AccountListResponse {
  readonly accounts: Account[]
}

export interface ResetPasswordRequest {
  readonly accountId: UUID<Account>;
  readonly token: string;
  readonly newPassword: string;
}

export function getAllAccounts(): Promise<Account[]> {
  return axios.get<AccountListResponse>('/accounts').then(({ data }) => data.accounts);
}

export function getMe(): Promise<Account> {
  return axios.get('/accounts/me').then(({ data }) => data);
}

export function postRegister(name: string, email: string, password: string): Promise<Account> {
  return axios.post<Account>('/accounts/register', { name, email, password }).then(({ data }) => data);
}

export function postLogin(email: string, password: string): Promise<Account> {
  return axios.post<Account>('/accounts/login', { email, password }).then(({ data }) => data);
}

export function postLogout(): Promise<any> {
  return axios.post("/accounts/logout")
}

export function requestRole(role: AccountRole): Promise<void> {
  return axios.patch("/accounts/roles", { role, intent: 'ADD' });
}

export function removeRole(role: AccountRole): Promise<void> {
  return axios.patch("/accounts/roles", { role, intent: 'REMOVE' });
}

export function impersonate(accountId: UUID<Account>): Promise<void> {
  return axios.post(`/accounts/impersonate/${accountId}`);
}

export function forgotPassword(email: string): Promise<void> {
  return axios.post('/accounts/password/forgot', { email });
}

export function resetPassword({ accountId, token, newPassword }: ResetPasswordRequest): Promise<Account> {
  return axios.post<Account>('/accounts/password/reset', { accountId, token, newPassword })
    .then(({ data }) => data);
}

export function getProfile(): Promise<Profile> {
  return axios.get<Profile>('/accounts/profile').then(({ data }) => data);
}

export function updateEmailPreference(receiveEmails: boolean): Promise<void> {
  return axios.patch('/accounts/profile/emails', { receiveEmails });
}

export function deactivateAccount(): Promise<void> {
  return axios.post('/accounts/deactivate');
}

export function adminDeactivateAccount(id: UUID<Account>): Promise<void> {
  return axios.post(`/accounts/deactivate/${id}`);
}

export function adminReactivateAccount(id: UUID<Account>): Promise<void> {
  return axios.post(`/accounts/reactivate/${id}`);
}

export interface AccountUpdateError {
  readonly reason: string;
  readonly details: string;
}

export function accountUpdateError(error: any): AccountUpdateError | undefined {
  const data = error?.response?.data;
  return data?.reason !== undefined ? data : undefined;
}

export function updateName(name: string): Promise<Account> {
  return axios.patch<Account>('/accounts/me/name', { name }).then(({ data }) => data);
}

export function changePassword(currentPassword: string, newPassword: string): Promise<Account> {
  return axios.post<Account>('/accounts/me/password', { currentPassword, newPassword }).then(({ data }) => data);
}

export function requestEmailChange(currentPassword: string, newEmail: string): Promise<void> {
  return axios.post('/accounts/me/email', { currentPassword, newEmail });
}

export function confirmEmailChange(token: string): Promise<void> {
  return axios.post('/accounts/email/confirm', { token });
}

export function adminChangeEmail(id: UUID<Account>, email: string): Promise<Account> {
  return axios.patch<Account>(`/accounts/${id}/email`, { email }).then(({ data }) => data);
}
